import { forwardRef, useEffect } from 'react';

import { Box, Button as MuiButton, CircularProgress } from '@mui/material';

import type { ButtonProps } from 'components/button/type';

import { acquireInteractionBlock } from 'utils/interactionBlockManager';

import { sizeMap, createButtonSx } from 'styles/button';

const renderLoadingSpinner = () => (
  <CircularProgress size={16} color="inherit" aria-label="Loading" />
);

const renderLoadingContentWithPreservedSize = (children: React.ReactNode) => (
  <Box
    component="span"
    sx={{
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <CircularProgress
      size={16}
      color="inherit"
      aria-label="Loading"
      sx={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        marginLeft: '-8px',
        marginTop: '-8px',
      }}
    />
    <Box component="span" sx={{ visibility: 'hidden' }}>
      {children}
    </Box>
  </Box>
);

const renderButtonContent = (
  loading: boolean,
  loadingPreserveSize: boolean,
  children: React.ReactNode
): React.ReactNode => {
  if (!loading) {
    return children;
  }

  if (loadingPreserveSize) {
    return renderLoadingContentWithPreservedSize(children);
  }

  return renderLoadingSpinner();
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'medium',
      height,
      width,
      sx,
      loading = false,
      loadingPreserveSize = false,
      blockInteraction = true,
      ...props
    },
    ref
  ) => {
    const muiVariant = variant === 'link' || variant === 'text' ? 'text' : 'contained';
    const muiSize = sizeMap[size];
    const customSx = createButtonSx(variant, size, height, width, sx);

    useEffect(() => {
      if (!loading || !blockInteraction) {
        return undefined;
      }

      return acquireInteractionBlock();
    }, [blockInteraction, loading]);

    return (
      <MuiButton
        ref={ref}
        variant={muiVariant}
        size={muiSize}
        data-variant={variant}
        data-size={size}
        sx={customSx}
        {...props}
        disabled={props.disabled || loading}
        aria-busy={loading || undefined}
      >
        {renderButtonContent(loading, loadingPreserveSize, children)}
      </MuiButton>
    );
  }
);

Button.displayName = 'Button';

export default Button;
